import Grid from "@mui/material/Grid";
import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { ExpandedSimulationContext } from "../App";
import { useMode } from "../mode/ModeContext";
import { modes } from "../mode/modes";
import { MortgageSimulatorGroup } from "../simulator";
import { SimulationFormModel } from "../types/forms";
import SimulationPage from "./SimulationPage";
import SortableSimulationCard from "./SortableSimulationCard";

export default function SimulationGrid({
  simulations,
  simulationFormModel,
  handleEdit,
  handleDuplicate,
  handleDelete,
}: {
  simulations: MortgageSimulatorGroup;
  simulationFormModel: SimulationFormModel;
  handleEdit?: (index: number) => void;
  handleDuplicate?: (index: number) => number;
  handleDelete?: (index: number) => void;
}) {
  const { expanded, setExpanded } = React.useContext(ExpandedSimulationContext);
  const { mode } = useMode();
  const ModeInsights = mode ? modes[mode].components.ModeInsights : null;

  // Calculate if delete should be disabled based on mode's minimum simulations
  const canDelete = mode ? (modes[mode].canDelete ?? true) : true;
  const minimumSimulations = mode ? (modes[mode].minimumSimulations ?? 1) : 1;
  const deleteDisabled =
    !canDelete || simulations.simulations.length <= minimumSimulations;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = simulations.simulations.findIndex(
        (_, idx) => `sim-${idx}` === active.id,
      );
      const newIndex = simulations.simulations.findIndex(
        (_, idx) => `sim-${idx}` === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        simulationFormModel.moveSimulation(oldIndex, newIndex);
      }
    }
  };

  const handleDuplicateAndSwitch = React.useMemo(() => {
    if (!handleDuplicate) return undefined;
    return (idx: number) => {
      setExpanded(null);
      const newIdx = handleDuplicate(idx);
      setExpanded(newIdx);
      return newIdx;
    };
  }, [handleDuplicate, setExpanded]);

  const handleDeleteAndCollapse = React.useMemo(() => {
    if (!handleDelete) return undefined;
    return (idx: number) => {
      setExpanded(null);
      handleDelete(idx);
    };
  }, [handleDelete, setExpanded]);

  if (expanded !== null) {
    const sim = simulations.getSimulation(expanded);
    return (
      <SimulationPage
        sim={sim}
        moreExpensive={simulations.moreExpensiveBy(expanded)}
        idx={expanded}
        handleEdit={handleEdit}
        handleDuplicate={handleDuplicateAndSwitch}
        handleDelete={handleDeleteAndCollapse}
        toggleExpanded={() => {
          setExpanded(null);
        }}
        deleteDisabled={deleteDisabled}
      />
    );
  }

  return (
    <>
      {ModeInsights && <ModeInsights model={simulationFormModel} />}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={simulations.simulations.map((_, idx) => `sim-${idx}`)}
          strategy={rectSortingStrategy}
        >
          <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
            {simulations.simulations.map((sim, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                <SortableSimulationCard
                  id={`sim-${idx}`}
                  sim={sim}
                  moreExpensive={simulations.moreExpensiveBy(idx)}
                  idx={idx}
                  handleEdit={handleEdit}
                  handleDuplicate={handleDuplicate}
                  handleDelete={handleDelete}
                  toggleExpanded={() => {
                    setExpanded(idx);
                  }}
                  deleteDisabled={deleteDisabled}
                />
              </Grid>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
    </>
  );
}
